import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Card } from "react-bootstrap";
import { FiDollarSign, FiCreditCard, FiLogOut } from "react-icons/fi";
import { useStore } from "../../app/stores/store";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { PartnerpaymentType } from "../../lib/utils";
import PagesDropdown from "../../components/Dropdowns/PagesDropdown";
import Sidebar from "../../components/Sidebar/Sidebar.js";
import UserDropdown from "../../components/Dropdowns/UserDropdown";
import Footer from "../common/footer";
import PartnerTransactions from "./PartnerListTransactions";

const PartnerHome = observer(() => {
  const { userStore, paymentStore } = useStore();
  const navigate = useNavigate();
  const [depositAmount, setDepositAmount] = useState('');
  const [paymentType, setPaymentType] = useState(''); // 'direct', 'bank', or 'online'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingPayments, setPendingPayments] = useState([]);
  const { id } = useParams<{ id: string }>();
  const [locationPathname, setLocationPathname] = useState(location.pathname);
  const [navbarOpen, setNavbarOpen] = React.useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);

useEffect(() => {
  if (userStore.isLoggedIn) {
    (async () => {
      await userStore.getUser();
    })();
  }
}, []);

  const isValidAmount =
    depositAmount &&
    (paymentType === "online" ? parseFloat(depositAmount) >= 10000 : parseFloat(depositAmount) > 0);

  const onlineCharge = paymentType === "online" && isValidAmount
    ? (parseFloat(depositAmount) * 0.03).toFixed(2)
    : "0.00";

  const totalAmount = paymentType === "online" && isValidAmount
    ? (parseFloat(depositAmount) * 1.03).toFixed(2)
    : depositAmount && parseFloat(depositAmount) >= 10000
      ? parseFloat(depositAmount).toFixed(2)
      : "0.00";


  const handleLogout = () => {
    userStore.logout();
    navigate("/login");
  };

  const onConfirmClick = () => {
    setError('');
    handleConfirmSecurityDeposit();
  };

  const handleConfirmSecurityDeposit = async () => {
  setError('');

  const user = userStore.user;

  if (!user || !user.id) {
    setError("User information is missing. Please log in again.");
    return;
  }

  if (!paymentType) {
    setError("You must choose a payment method.");
    return;
  }

  const type: PartnerpaymentType = { type: paymentType };

  if (paymentType === "bank") {
    if (!receiptFile) {
      setError("Please upload a receipt for the bank transfer.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("Type", "partner");
      formData.append("IDS", user.id);
      formData.append("PaymentMethod", "bank");
      formData.append("Receipt", receiptFile);

      await userStore.setPaymentTypeOfPartner(user.id, type);
      await paymentStore.uploadBankTransferReceipt(formData); // <-- Implement this method
      toast.success("Bank receipt uploaded and payment method set.");
    } catch (err) {
      toast.error("Error uploading bank transfer receipt.");
    }
    return;
  }

  if (paymentType === "direct") {
    try {
      await userStore.setPaymentTypeOfPartner(user.id, type);
      toast.success(`Deposit payment method set to ${paymentType}`);
    } catch (err) {
      toast.error("Error updating payment method.");
    }
    return;
  }

  if (paymentType === "online") {
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      setError("Please enter a valid deposit amount.");
      return;
    }

    if (parseFloat(depositAmount) < 10000) {
      setError("Minimum deposit amount for online payment is AED 10,000.");
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append("Type", "partner");
    formData.append("TransactionAmount", parseFloat(totalAmount).toFixed(2));
    formData.append("IDS", user.id);
    formData.append("OrderName", "SecurityDeposit");

    try {
      const result = await paymentStore.initiatePayment(formData);
      if (result?.status === "success") {
        await userStore.setPaymentTypeOfPartner(user.id, type);
        window.location.href = result.data as string;
      } else {
        toast.error("Payment initiation failed!");
        toast.error(`Payment initiation failed: ${result?.error || "Unknown error"}`);
      }
    } catch (err) {
      toast.error("Error initiating payment.");
    } finally {
      setIsLoading(false);
    }
  }
};


  return (
    <>
      <Sidebar />

      <div className="relative md:ml-64 bg-blueGray-100">
        {/* Header */}
        <div className="relative bg-[#b60072] md:pt-2 pb-32 pt-12">
          <div className="px-4 md:px-10 mx-auto w-full">
            {userStore.isLoggedIn && (
              <div className="d-flex justify-content-between align-items-center px-4 py-3">
                <span style={{ color: "#fff" }}>
                  Welcome, {userStore.user?.displayName || 'Partner'}
                </span>
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleLogout}
                  className="d-flex align-items-center gap-2"
                  style={{ color: "#b60072", fontWeight: "bold" }}
                >
                  <FiLogOut size={16} /> Logout
                </Button>
              </div>
            )}



            <div>
              <div className="flex flex-wrap">
                <div className="w-full lg:w-6/12 xl:w-3/12 px-4">
                  <div className="relative flex flex-col min-w-0 break-words bg-white rounded mb-6 xl:mb-0 shadow-lg">
                    <div className="flex-auto p-4">
                      <div className="flex flex-wrap">
                        <div className="relative w-full pr-4 max-w-full flex-grow flex-1">
                          <h5 className="text-blueGray-400 uppercase font-bold text-xs">Wallet Overview</h5>
                          <span className="font-semibold text-xl text-blueGray-700">10000</span>
                        </div>
                        <div className="relative w-auto pl-4 flex-initial">
                          <div className="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 shadow-lg rounded-full bg-red-500">
                            <i className="far fa-chart-bar">
                            </i>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-blueGray-400 mt-4">
                      </p>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-6/12 xl:w-3/12 px-4">
                  <div className="relative flex flex-col min-w-0 break-words bg-white rounded mb-6 xl:mb-0 shadow-lg">
                    <div className="flex-auto p-4">
                      <div className="flex flex-wrap">
                        <div className="relative w-full pr-4 max-w-full flex-grow flex-1">
                          <h5 className="text-blueGray-400 uppercase font-bold text-xs">Pending</h5>
                          <span className="font-semibold text-xl text-blueGray-700">350,897</span>
                        </div>
                        <div className="relative w-auto pl-4 flex-initial">
                          <div className="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 shadow-lg rounded-full bg-red-500">
                            <i className="far fa-chart-bar"></i></div></div></div><p className="text-sm text-blueGray-400 mt-4">

                      </p></div>
                  </div></div>


                <div className="w-full lg:w-6/12 xl:w-3/12 px-4">
                  <div className="relative flex flex-col min-w-0 break-words bg-white rounded mb-6 xl:mb-0 shadow-lg">
                    <div className="flex-auto p-4">
                      <div className="flex flex-wrap">
                        <div className="relative w-full pr-4 max-w-full flex-grow flex-1">
                          <h5 className="text-blueGray-400 uppercase font-bold text-xs">Rejected</h5>
                          <span className="font-semibold text-xl text-blueGray-700">350,897</span>
                        </div>
                        <div className="relative w-auto pl-4 flex-initial">
                          <div className="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 shadow-lg rounded-full bg-red-500">
                            <i className="far fa-chart-bar"></i></div></div></div><p className="text-sm text-blueGray-400 mt-4">


                      </p>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-6/12 xl:w-3/12 px-4">
                  <div className="relative flex flex-col min-w-0 break-words bg-white rounded mb-6 xl:mb-0 shadow-lg">
                    <div className="flex-auto p-4">
                      <div className="flex flex-wrap">
                        <div className="relative w-full pr-4 max-w-full flex-grow flex-1">
                          <h5 className="text-blueGray-400 uppercase font-bold text-xs">TRAFFIC</h5>
                          <span className="font-semibold text-xl text-blueGray-700">350,897</span>
                        </div>
                        <div className="relative w-auto pl-4 flex-initial">
                          <div className="text-white p-3 text-center inline-flex items-center justify-center w-12 h-12 shadow-lg rounded-full bg-red-500">
                            <i className="far fa-chart-bar"></i></div></div></div><p className="text-sm text-blueGray-400 mt-4">
                      </p>
                    </div>
                  </div>
                </div>


              </div>
            </div>
          </div>
        </div>


        <PartnerTransactions />
        <div className="row mb-5 px-4 py-3">

          {/* Left Card: Security Deposit */}
          <div className="col-lg-6 mb-4">
            <Card className="h-100 shadow-sm border-0">
              <Card.Header className="bg-[gradient] bg-info text-white rounded-top py-3 px-4">
                <h5 className="mb-0">💼 Security Deposit / Payment</h5>
              </Card.Header>
              <Card.Body className="p-4">

                {error && <div className="alert alert-danger">{error}</div>}



                <div className="mb-4">
                  <label className="form-label fw-semibold mb-2">Choose Payment Method</label>
                  <div className="d-flex flex-wrap gap-3">
                    {/* Direct Payment */}
                    <label className="d-flex align-items-center border border-secondary-subtle rounded shadow-sm p-2 px-3 cursor-pointer">
                      <i className="fas fa-money-bill-wave text-success me-2"></i>
                      <input
                        type="radio"
                        value="direct"
                        checked={paymentType === "direct"}
                        onChange={(e) => setPaymentType(e.target.value)}
                        className="me-2"
                      />
                      Cash Payment
                    </label>

                    {/* Online Payment */}
                    <label className="d-flex align-items-center border border-secondary-subtle rounded shadow-sm p-2 px-3 cursor-pointer">
                      <img src="/assets/img/etisalatLogo.gif" className="me-2" style={{ width: 24, height: 24 }} alt="Online" />
                      <input
                        type="radio"
                        value="online"
                        checked={paymentType === "online"}
                        onChange={(e) => setPaymentType(e.target.value)}
                        className="me-2"
                      />
                      Online Payment
                    </label>

                    {/* Bank Transfer */}
                    <label className="d-flex align-items-center border border-secondary-subtle rounded shadow-sm p-2 px-3 cursor-pointer">
                      <img src="/assets/img/etisalatLogo.gif" className="me-2" style={{ width: 24, height: 24 }} alt="Bank" />
                      <input
                        type="radio"
                        value="bank"
                        checked={paymentType === "bank"}
                        onChange={(e) => setPaymentType(e.target.value)}
                        className="me-2"
                      />
                      Bank Transfer
                    </label>
                  </div>
                </div>

                {/* Bank Details Section */}
                {paymentType === "bank" && (
                  <div className="alert alert-light border border-secondary-subtle">
                    <h6 className="fw-bold mb-2">🏦 Bank Transfer Details</h6>
                    <p className="mb-1"><strong>Bank:</strong> Wio Bank</p>
                    <p className="mb-1"><strong>Account:</strong> UBER TRAVEL AGENCY L.L.C</p>
                    <p className="mb-1"><strong>Number:</strong> 09189995985</p>
                    <p className="mb-1"><strong>IBAN:</strong> AE920860000009189995985</p>
                    <p className="mb-0"><strong>SWIFT:</strong> WIOBAEDXXX</p>
                  </div>
                )}

                <div className="mb-4">
                  <label className="form-label fw-semibold">Deposit Amount (AED)</label>

                  {paymentType === "direct" && (
                    <div className="alert alert-info mt-2 p-3">
                      💼 Please proceed to the center to pay the security deposit.
                    </div>
                  )}

                  {paymentType === "bank" && (
                    <>
                      <div className="alert alert-info mt-2 p-3">
                        💼 Once the security deposit of minimum AED 10,000 is transferred to the below mentioned account, please attach the receipt here.
                      </div>

                      {/* Receipt upload */}
                      <label className="form-label fw-semibold">Upload Receipt</label>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        className="form-control"
                        onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                        style={{ maxWidth: "220px" }}
                      />
                    </>
                  )}

                  {paymentType === "online" && (
                    <>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control shadow-sm"
                        placeholder="Enter deposit amount"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        style={{ maxWidth: "220px" }}
                      />
                      {depositAmount && (
                        <div className="alert alert-info mt-3 p-3">
                          <p className="mb-1"><strong>🧾 3% Online Payment Fee:</strong> AED {onlineCharge}</p>
                          <p className="mb-0"><strong>💰 Total Payable:</strong> AED {totalAmount}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>


                <Button
                  variant="primary"
                  disabled={isLoading}
                  onClick={onConfirmClick}
                  className="mt-3"
                >
                  {isLoading ? "Processing..." : "✅ Confirm Deposit"}
                </Button>
              </Card.Body>
            </Card>
          </div>

          {/* Right Card: Partner Info */}
          <div className="col-lg-6 mb-4">
            <Card className="h-100 shadow-sm border-0">
              <Card.Header className="bg-gradient bg-info text-white rounded-top py-3 px-4">
                <h5 className="mb-0">📄 Partner Payment Details</h5>
              </Card.Header>
              <Card.Body className="p-4">
                <div className="table-responsive">
                  <table className="table table-bordered align-middle text-center mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Partner Name</th>
                        <th>Email</th>
                        <th>Payment Method</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{userStore.user?.displayName || "N/A"}</td>
                        <td>{userStore.user?.email || "N/A"}</td>
                        <td>{userStore.user?.securityDepositPaymentType}</td>
                        <td>
                       {userStore.user?.securityDepositPaid === true ? (
  <span className="badge bg-success">✅ Paid</span>
) : (
  <span className="badge bg-warning text-dark">⏳ Pending</span>
)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
});

export default PartnerHome;